import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { MapPin, Calendar, Clock, CheckCircle2, AlertCircle } from 'lucide-react';
import api from '../../services/api';

function formatData(iso) {
  if (!iso) return '';
  // Coluna `date` do Postgres volta como timestamp ISO completo — slice pra pegar só a data.
  return new Date(iso.slice(0, 10) + 'T00:00:00').toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit' });
}

function nomeCompletoValido(nome) {
  const partes = (nome || '').trim().split(/\s+/).filter(Boolean);
  return partes.length >= 2 && partes.every(p => p.length >= 2);
}

function emailValido(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || '').trim());
}

function formatWhatsapp(value) {
  let digits = (value || '').replace(/\D/g, '');
  if (digits.length > 11 && digits.startsWith('55')) digits = digits.slice(2);
  return digits;
}

export default function EventoPublico() {
  const { id } = useParams();
  const [dia, setDia] = useState('');
  const [horarioId, setHorarioId] = useState('');
  const [nome, setNome] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [email, setEmail] = useState('');
  const [vinculo, setVinculo] = useState(''); // 'autonomo' | 'imobiliaria'
  const [imobiliaria, setImobiliaria] = useState('');
  const [erro, setErro] = useState('');
  const [concluido, setConcluido] = useState(null);
  const [conflito, setConflito] = useState(null); // { dia, horario } — já existe agendamento nesse WhatsApp

  const { data: evento, isLoading, isError } = useQuery({
    queryKey: ['evento-publico', id],
    queryFn: () => api.get(`/eventos/publico/${id}`).then(r => r.data.data),
    retry: false,
  });

  const dias = [...new Set((evento?.horarios || []).map(h => h.dia))].sort();
  useEffect(() => { if (dias.length && !dia) setDia(dias[0]); }, [dias, dia]);

  const horariosDoDia = (evento?.horarios || []).filter(h => h.dia === dia).sort((a, b) => a.horario.localeCompare(b.horario));
  const horariosComVaga = horariosDoDia.filter(h => h.vagas_restantes > 0);
  // Evento com um único dia / um único horário (ex: Parrilla): não faz sentido obrigar a escolher —
  // já vem selecionado e o seletor some.
  const diaUnico = dias.length <= 1;
  const autoHorarioId = horariosComVaga.length === 1 ? horariosComVaga[0].id : null;
  // Só esconde o seletor de horário quando há exatamente um horário e ele tem vaga (auto-selecionado).
  const horarioUnico = horariosDoDia.length === 1 && horariosComVaga.length === 1;

  useEffect(() => {
    if (autoHorarioId && !horarioId) setHorarioId(autoHorarioId);
  }, [autoHorarioId, horarioId]);

  const agendarMut = useMutation({
    mutationFn: ({ reagendar = false } = {}) => api.post(`/eventos/publico/${id}/confirmacoes`, {
      nome: nome.trim(),
      whatsapp: whatsapp.trim(),
      email: email.trim(),
      vinculo_tipo: vinculo,
      imobiliaria_nome: vinculo === 'imobiliaria' ? imobiliaria.trim() : null,
      evento_horario_id: horarioId,
      reagendar,
    }),
    onSuccess: (res) => { setConflito(null); setConcluido(res.data.data); },
    onError: (err) => {
      const d = err?.response?.data;
      if (d?.ja_agendado) { setConflito(d.agendamento || {}); setErro(''); }
      else setErro(d?.error || 'Não foi possível confirmar. Tente novamente.');
    },
  });

  const confirmar = () => {
    setErro('');
    if (!nome.trim() || !whatsapp.trim()) return setErro('Preencha nome e WhatsApp');
    if (!nomeCompletoValido(nome)) return setErro('Digite nome e sobrenome');
    if (!emailValido(email)) return setErro('Digite um e-mail válido');
    if (!vinculo) return setErro('Informe se você é corretor autônomo ou de imobiliária');
    if (vinculo === 'imobiliaria' && !imobiliaria.trim()) return setErro('Digite o nome da imobiliária');
    if (!horarioId) return setErro(conflito ? 'Escolha o novo dia e horário' : 'Escolha um horário');
    agendarMut.mutate({ reagendar: !!conflito });
  };

  const manterAgendamento = () => {
    setConflito(null);
    setConcluido({ dia: conflito?.dia, horario: conflito?.horario, mantido: true });
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-start justify-center p-4 py-10">
      <div className="w-full max-w-md">
        {isLoading && <div className="text-center text-gray-400 py-16">Carregando...</div>}

        {isError && (
          <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
            <AlertCircle className="mx-auto text-red-400 mb-3" size={32} />
            <p className="text-gray-700 font-medium">Este link de agendamento não está mais disponível.</p>
          </div>
        )}

        {evento && !concluido && (
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            {evento.imagem_url && (
              <img src={evento.imagem_url} alt={evento.nome} className="w-full max-h-72 object-cover" />
            )}
            <div className="bg-primary text-white px-6 py-5">
              <h1 className="text-lg font-bold">{evento.nome}</h1>
              {evento.local && <p className="text-sm text-white/80 flex items-center gap-1 mt-1"><MapPin size={13} /> {evento.local}</p>}
            </div>

            <div className="p-6 space-y-5">
              {evento.descricao && <p className="text-sm text-gray-600 whitespace-pre-line">{evento.descricao}</p>}

              {dias.length === 0 ? (
                <p className="text-sm text-gray-400">Nenhum horário disponível no momento.</p>
              ) : evento.vagas_esgotadas ? (
                <p className="text-sm text-gray-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-3">
                  As vagas para este evento já foram preenchidas. Se você já recebeu um convite pessoal, use o link do convite pra confirmar.
                </p>
              ) : (
                <>
                  {(diaUnico || horarioUnico) && horariosComVaga.length > 0 && (
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-700 bg-gray-50 rounded-lg px-3 py-2">
                      <span className="flex items-center gap-1"><Calendar size={13} className="text-gray-400" /> {formatData(dia)}</span>
                      {horarioUnico && <span className="flex items-center gap-1"><Clock size={13} className="text-gray-400" /> {horariosDoDia[0].horario.slice(0, 5)}</span>}
                    </div>
                  )}

                  {!diaUnico && (
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-2 flex items-center gap-1"><Calendar size={13} /> Dia</label>
                      <div className="flex flex-wrap gap-2">
                        {dias.map(d => (
                          <button
                            key={d}
                            type="button"
                            onClick={() => { setDia(d); setHorarioId(''); }}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium border capitalize ${dia === d ? 'bg-primary text-white border-primary' : 'bg-white text-gray-600 border-gray-200'}`}
                          >
                            {formatData(d)}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {!horarioUnico && (
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-2 flex items-center gap-1"><Clock size={13} /> Horário</label>
                      <div className="grid grid-cols-3 gap-2">
                        {horariosDoDia.map(h => {
                          const lotado = h.vagas_restantes <= 0;
                          return (
                            <button
                              key={h.id}
                              type="button"
                              disabled={lotado}
                              onClick={() => setHorarioId(h.id)}
                              className={`py-2 rounded-lg text-sm font-medium border ${
                                lotado ? 'bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed'
                                : horarioId === h.id ? 'bg-primary text-white border-primary'
                                : 'bg-white text-gray-700 border-gray-200'
                              }`}
                            >
                              {h.horario.slice(0, 5)}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="pt-2 border-t border-gray-100 space-y-3">
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Nome e sobrenome</label>
                      <input className="input-field" placeholder="ex: João Silva" value={nome} onChange={e => setNome(e.target.value)} />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">WhatsApp</label>
                      <input className="input-field" placeholder="(44) 99999-9999" value={whatsapp} onChange={e => { setWhatsapp(formatWhatsapp(e.target.value)); if (conflito) setConflito(null); }} />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">E-mail</label>
                      <input className="input-field" type="email" inputMode="email" autoCapitalize="none" placeholder="ex: joao@email.com" value={email} onChange={e => setEmail(e.target.value)} />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Você é corretor(a)...</label>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => setVinculo('autonomo')}
                          className={`py-2 rounded-lg text-sm font-medium border ${vinculo === 'autonomo' ? 'bg-primary text-white border-primary' : 'bg-white text-gray-700 border-gray-200'}`}
                        >
                          Autônomo
                        </button>
                        <button
                          type="button"
                          onClick={() => setVinculo('imobiliaria')}
                          className={`py-2 rounded-lg text-sm font-medium border ${vinculo === 'imobiliaria' ? 'bg-primary text-white border-primary' : 'bg-white text-gray-700 border-gray-200'}`}
                        >
                          De imobiliária
                        </button>
                      </div>
                    </div>
                    {vinculo === 'imobiliaria' && (
                      <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Qual imobiliária?</label>
                        <input className="input-field" placeholder="Digite o nome da imobiliária" value={imobiliaria} onChange={e => setImobiliaria(e.target.value)} />
                      </div>
                    )}
                    {conflito && (
                      <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
                        <p className="font-semibold flex items-center gap-1"><AlertCircle size={14} /> Você já tem um agendamento</p>
                        <p className="mt-1">
                          Esse WhatsApp já está agendado
                          {conflito.dia ? ` para ${formatData(conflito.dia)}` : ''}
                          {conflito.horario ? ` às ${conflito.horario.slice(0, 5)}` : ''}.
                        </p>
                        <p className="mt-1">Para trocar, escolha o novo dia e horário acima e toque em <strong>Reagendar</strong>.</p>
                      </div>
                    )}
                    {erro && <p className="text-sm text-red-600">{erro}</p>}
                    <button
                      onClick={confirmar}
                      disabled={agendarMut.isPending}
                      className="btn-primary w-full py-3 text-base disabled:opacity-50"
                    >
                      {agendarMut.isPending ? (conflito ? 'Reagendando...' : 'Confirmando...') : (conflito ? 'Reagendar' : 'Confirmar presença')}
                    </button>
                    {conflito && (
                      <button
                        type="button"
                        onClick={manterAgendamento}
                        disabled={agendarMut.isPending}
                        className="w-full py-2 text-sm font-medium text-gray-500 hover:text-gray-700 disabled:opacity-50"
                      >
                        Manter meu agendamento atual
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {concluido && (
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <CheckCircle2 className="mx-auto text-green-500 mb-3" size={40} />
            <h2 className="text-lg font-bold text-gray-800">
              {concluido.mantido ? 'Agendamento mantido' : concluido.reagendado ? 'Agendamento remarcado!' : 'Presença confirmada!'}
            </h2>
            <p className="text-sm text-gray-500 mt-2">
              {formatData(concluido.dia)} às {concluido.horario?.slice(0, 5)}.
              {concluido.mantido
                ? ' Seu agendamento continua o mesmo.'
                : ` Você vai receber a confirmação${concluido.reagendado ? ' atualizada' : ''} com o QR Code no seu WhatsApp — apresente ele na chegada.`}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
